import { useEffect, useState, type FormEvent } from 'react';
import { activeOrganizationId, graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

type Address = { line1: string; line2?: string | null; city: string; state: string; postalCode: string };
const blank: Address = { line1: '', line2: '', city: '', state: '', postalCode: '' };

/** The company's mailing address, printed on every notice as the lienor's (or supplier's) address. */
export function CompanyAddress() {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [address, setAddress] = useState<Address>(blank);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [contact, setContact] = useState({ phone: '', email: '' });
  const [contactMessage, setContactMessage] = useState('');
  const [contactError, setContactError] = useState('');
  useEffect(() => {
    if (!token) return;
    graphql<{ companyProfile: { name: string; mailingAddress: Address | null; contactPhone: string | null; contactEmail: string | null } }>({
      query: 'query { companyProfile { name mailingAddress contactPhone contactEmail } }', token, organizationId: activeOrganizationId() })
      .then(data => { setName(data.companyProfile.name); setAddress({ ...blank, ...(data.companyProfile.mailingAddress ?? {}) });
        setContact({ phone: data.companyProfile.contactPhone ?? '', email: data.companyProfile.contactEmail ?? '' }); })
      .catch((reason: Error) => setError(reason.message));
  }, [token]);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    setMessage(''); setError('');
    try {
      await graphql({ query: 'mutation Address($address:JSON!){updateCompanyMailingAddress(address:$address){name}}', token, organizationId: activeOrganizationId(),
        variables: { address: { line1: address.line1.trim(), line2: address.line2?.trim() || null, city: address.city.trim(), state: address.state.trim().toUpperCase(), postalCode: address.postalCode.trim() } } });
      setMessage('Saved. Your notices will show this address.');
    } catch (reason) { setError((reason as Error).message); }
  }
  async function saveContact(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    setContactMessage(''); setContactError('');
    try {
      const saved = await graphql<{ updateCompanyContact: { contactPhone: string | null; contactEmail: string | null } }>({
        query: 'mutation Contact($contact:JSON!){updateCompanyContact(contact:$contact){contactPhone contactEmail}}', token, organizationId: activeOrganizationId(),
        variables: { contact: { phone: contact.phone.trim() || null, email: contact.email.trim() || null } } });
      setContact({ phone: saved.updateCompanyContact.contactPhone ?? '', email: saved.updateCompanyContact.contactEmail ?? '' });
      setContactMessage('Saved. Notices that ask for your telephone or email will show these.');
    } catch (reason) { setContactError((reason as Error).message); }
  }
  const field = (key: keyof Address, label: string, props: Record<string, unknown> = {}) => <label>{label}
    <input value={address[key] ?? ''} onChange={event => setAddress({ ...address, [key]: event.target.value })} {...props} /></label>;
  return <section className="workspace-section" aria-label="Company mailing address">
    <h2>{name ? `${name}’s mailing address` : 'Company mailing address'}</h2>
    <p>Every notice PayRight prepares for your company prints this address as yours. Owners and administrators can change it.</p>
    <form className="inline-card settings-form" onSubmit={event => void save(event)}>
      {field('line1', 'Street address', { required: true })}
      {field('line2', 'Suite or unit (optional)')}
      {field('city', 'City', { required: true })}
      {field('state', 'State', { required: true, pattern: '[A-Za-z]{2}', maxLength: 2, title: 'Use the two-letter state code, like FL' })}
      {field('postalCode', 'ZIP code', { required: true, pattern: '[0-9]{5}(-[0-9]{4})?', title: 'Use a five-digit ZIP code, like 33602' })}
      <button className="button">Save address</button>
      {message && <p role="status">{message}</p>}
      {error && <div className="alert error">{error}</div>}
    </form>
    <h3>Telephone and email</h3>
    <p>Some states’ notices print the claimant’s telephone number or email address. PayRight prints these where a notice asks for them.</p>
    <form className="inline-card settings-form" onSubmit={event => void saveContact(event)}>
      <label>Telephone<input type="tel" value={contact.phone} onChange={event => setContact({ ...contact, phone: event.target.value })}
        pattern="[0-9 \(\)\+\.\-]{10,20}" title="Enter a ten-digit US telephone number, like (813) 555-0100" maxLength={20} /></label>
      <label>Email<input type="email" value={contact.email} onChange={event => setContact({ ...contact, email: event.target.value })} maxLength={254} /></label>
      <button className="button">Save telephone and email</button>
      {contactMessage && <p role="status">{contactMessage}</p>}
      {contactError && <div className="alert error">{contactError}</div>}
    </form>
  </section>;
}
